import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';

import { ApiService } from '../../../../core/services/api.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { DeviceIdentityService } from '../../../../core/services/device-identity.service';
import { HubRoutingService } from '../../../../core/offline/hub-routing.service';
import { HubDiscoveryService } from '../../../../core/offline/hub-discovery.service';
import { NetworkStatusService } from '../../../../core/offline/network-status.service';
import { OfflineSessionGuardService } from '../../../../core/offline/offline-session-guard.service';
import { PosStateService } from '../../pos-state.service';
import {
  ApprovalDialogConfig,
  ApprovalDialogResult,
  ManagerApprovalDialogComponent,
} from '../manager-approval-dialog/manager-approval-dialog.component';

type Direction = 'offline' | 'online';

/**
 * OF-SWITCH (2026-10-08) — ReconnectMode ConfirmBothWays, the store default.
 *
 * Internet lost → a blocking "Go Offline?" dialog on this terminal. Until
 * someone confirms, requests stay on the cloud and fail; work in progress is
 * started again once offline mode is on. Internet back → a "Go Online" badge;
 * confirming it swaps the cashier's hub-minted session for a cloud one (no
 * PIN), waits for the hub to finish uploading, then returns to the cloud.
 *
 * Both directions need `pos.offline.switch`, or a supervisor's approval via
 * the standard manager-approval dialog (approved on the hub — the cloud is
 * the thing that is unreachable). Each terminal confirms for itself.
 */
@Component({
  selector: 'app-offline-switch',
  standalone: true,
  imports: [CommonModule, TranslocoModule, ManagerApprovalDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Internet lost: confirm offline mode. Blocking on purpose, and over the
         POS lock: see .osw-over-lock. -->
    <div class="osw-scrim osw-over-lock" *ngIf="showGoOffline" role="alertdialog" aria-modal="true"
         aria-labelledby="oswOfflineTitle">
      <div class="osw-card">
        <h2 id="oswOfflineTitle">{{ 'pos.offlineSwitch.lostTitle' | transloco }}</h2>
        <p>{{ 'pos.offlineSwitch.lostBody' | transloco }}</p>
        <p class="osw-warn" *ngIf="!hubReachable">{{ 'pos.offlineSwitch.hubUnreachable' | transloco }}</p>
        <p class="osw-error" *ngIf="error">{{ error }}</p>
        <div class="osw-actions">
          <button type="button" class="osw-secondary" (click)="retry()" [disabled]="busy">
            {{ 'pos.offlineSwitch.retry' | transloco }}
          </button>
          <button type="button" class="osw-primary" (click)="start('offline')"
                  [disabled]="busy || !hubReachable">
            {{ 'pos.offlineSwitch.goOffline' | transloco }}
          </button>
        </div>
      </div>
    </div>

    <!-- Internet back: a badge, never an interruption. -->
    <button type="button" class="osw-badge" *ngIf="showGoOnlineBadge" (click)="openGoOnline()">
      {{ 'pos.offlineSwitch.goOnlineBadge' | transloco }}
    </button>
    <div class="osw-badge osw-badge-pending" *ngIf="goOnlinePending" role="status">
      {{ 'pos.offlineSwitch.uploading' | transloco: { count: pendingActions } }}
    </div>

    <div class="osw-scrim" *ngIf="showGoOnlineConfirm" role="dialog" aria-modal="true"
         aria-labelledby="oswOnlineTitle">
      <div class="osw-card">
        <h2 id="oswOnlineTitle">{{ 'pos.offlineSwitch.backTitle' | transloco }}</h2>
        <p>{{ 'pos.offlineSwitch.backBody' | transloco: { count: pendingActions } }}</p>
        <p class="osw-error" *ngIf="error">{{ error }}</p>
        <div class="osw-actions">
          <button type="button" class="osw-secondary" (click)="showGoOnlineConfirm = false" [disabled]="busy">
            {{ 'pos.offlineSwitch.notNow' | transloco }}
          </button>
          <button type="button" class="osw-primary" (click)="start('online')" [disabled]="busy">
            {{ 'pos.offlineSwitch.goOnline' | transloco }}
          </button>
        </div>
      </div>
    </div>

    <app-manager-approval-dialog
      [visible]="showApproval"
      [config]="approvalConfig"
      (approved)="onApproved($event)"
      (cancelled)="onApprovalCancelled()">
    </app-manager-approval-dialog>
  `,
  styles: [`
    /* Everything here is position:fixed; the host takes no layout space. */
    :host { display: contents; }
    .osw-scrim { position: fixed; inset: 0; z-index: 9000; background: rgba(15, 23, 42, .55);
      display: flex; align-items: center; justify-content: center; padding: 16px; }
    /* "Go Offline?" and the supervisor approval it raises paint over the POS
       lock (--z-lock). Under ConfirmBothWays a terminal that locked before
       the internet dropped could otherwise neither switch to the hub nor
       unlock: the PIN check stays on the cloud until offline is confirmed.
       Still under the D1 blocker (--z-terminal-down). The Go Online badge
       stays under the lock and waits for the unlock. */
    .osw-over-lock { z-index: var(--z-offline-switch, 10150); }
    /* The element name outranks the dialog's own .approval-overlay rule,
       whose styles load later and would win a tie. */
    :host ::ng-deep app-manager-approval-dialog .approval-overlay {
      z-index: var(--z-offline-switch, 10150);
    }
    .osw-card { background: var(--surface, #fff); color: var(--text, #0f172a); border-radius: 12px;
      max-width: 420px; width: 100%; padding: 20px; box-shadow: 0 10px 30px rgba(0,0,0,.25); }
    .osw-card h2 { margin: 0 0 8px; font-size: 1.15rem; }
    .osw-card p { margin: 0 0 12px; line-height: 1.4; }
    .osw-warn { color: #b45309; }
    .osw-error { color: #b91c1c; }
    .osw-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 8px; }
    .osw-actions button { min-height: 44px; padding: 0 16px; border-radius: 8px; border: 0; font-weight: 600; }
    .osw-primary { background: #2563eb; color: #fff; }
    .osw-primary:disabled { opacity: .5; }
    .osw-secondary { background: #e2e8f0; color: #0f172a; }
    .osw-badge { position: fixed; top: 10px; inset-inline-end: 12px; z-index: 8000;
      background: #16a34a; color: #fff; border: 0; border-radius: 999px; padding: 8px 14px;
      font-weight: 600; box-shadow: 0 4px 12px rgba(0,0,0,.2); min-height: 40px; }
    .osw-badge-pending { background: #0369a1; }
  `],
})
export class OfflineSwitchComponent implements OnInit, OnDestroy {
  private routing = inject(HubRoutingService);
  private hub = inject(HubDiscoveryService);
  private net = inject(NetworkStatusService);
  private api = inject(ApiService);
  private perms = inject(PermissionService);
  private device = inject(DeviceIdentityService);
  private posState = inject(PosStateService);
  private sessionGuard = inject(OfflineSessionGuardService);
  private cdr = inject(ChangeDetectorRef);

  showGoOffline = false;
  showGoOnlineBadge = false;
  showGoOnlineConfirm = false;
  goOnlinePending = false;
  hubReachable = false;
  pendingActions = 0;
  busy = false;
  error: string | null = null;

  showApproval = false;
  approvalConfig: ApprovalDialogConfig | null = null;
  private pendingDirection: Direction | null = null;

  private sub?: Subscription;
  private timer?: ReturnType<typeof setInterval>;
  private exchangeFailed = false;

  ngOnInit(): void {
    this.sub = this.routing.routingInputs$().subscribe(() => this.refresh());
    // The drain finishing and the backlog count are not on routingInputs$;
    // a light tick keeps the badge and the pending state current.
    this.timer = setInterval(() => {
      this.routing.recomputeActive();
      this.refresh();
    }, 2000);
    this.refresh();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.timer) clearInterval(this.timer);
  }

  private refresh(): void {
    const wasPending = this.goOnlinePending;
    this.hubReachable = this.routing.canGoOffline();
    this.pendingActions = this.hub.drainableBacklog();
    this.showGoOffline = this.routing.needsGoOfflineConfirmation() && !this.showApproval;
    this.showGoOnlineBadge = this.routing.goOnlineAvailable() && !this.showGoOnlineConfirm;
    this.goOnlinePending = this.routing.isGoOnlinePending();

    // Go Online finished (back on the cloud). A session the hub could not
    // exchange was never authenticated by the cloud: sign in again.
    if (wasPending && !this.routing.isConfirmedOffline() && this.exchangeFailed) {
      this.exchangeFailed = false;
      this.sessionGuard.retireIfOfflineMinted();
    }
    this.cdr.markForCheck();
  }

  async retry(): Promise<void> {
    this.busy = true;
    this.error = null;
    try {
      await Promise.allSettled([this.net.acceptOperatorRetry(), this.hub.probeNow()]);
    } finally {
      this.busy = false;
      this.refresh();
    }
  }

  openGoOnline(): void {
    this.error = null;
    this.showGoOnlineConfirm = true;
    this.refresh();
  }

  /** Confirmed by the operator: the permission, or a supervisor. */
  start(direction: Direction, askSupervisor = false): void {
    this.error = null;
    // askSupervisor — the hub already refused this cashier's own permission,
    // so re-submitting without an approval would only be refused again.
    if (!askSupervisor && this.perms.hasPermission('pos.offline.switch')) {
      this.submit(direction, null);
      return;
    }
    this.pendingDirection = direction;
    // The approval must reach the hub while the cloud is down.
    if (direction === 'offline') this.routing.setSwitchFlowActive(true);
    this.approvalConfig = {
      actionCode: 'pos.offline.switch',
      actionLabel: direction === 'offline' ? 'Switch to offline mode' : 'Switch back online',
      actionLabelKey: direction === 'offline'
        ? 'pos.offlineSwitch.approveOffline'
        : 'pos.offlineSwitch.approveOnline',
      targetEntityType: 'Device',
      targetEntityId: this.deviceId(),
    };
    this.showApproval = true;
    this.refresh();
  }

  onApproved(result: ApprovalDialogResult): void {
    this.showApproval = false;
    const direction = this.pendingDirection;
    this.pendingDirection = null;
    if (!direction || !result.approved || !result.approvalToken) {
      this.routing.setSwitchFlowActive(false);
      this.refresh();
      return;
    }
    this.submit(direction, result.approvalToken);
  }

  onApprovalCancelled(): void {
    this.showApproval = false;
    this.pendingDirection = null;
    this.routing.setSwitchFlowActive(false);
    this.refresh();
  }

  private submit(direction: Direction, approvalToken: string | null): void {
    this.busy = true;
    if (direction === 'offline') this.routing.setSwitchFlowActive(true);
    const body: Record<string, unknown> = {
      direction,
      deviceId: this.deviceId(),
      pendingActions: this.hub.drainableBacklog(),
    };
    if (approvalToken) body['approvalToken'] = approvalToken;

    this.api.post<any>('pos/offline-switch', body).subscribe({
      next: (res) => {
        this.busy = false;
        if (!res?.success) {
          this.routing.setSwitchFlowActive(false);
          if (res?.overrideEligible && !approvalToken) {
            // The hub's own gate said no (permissions differ from the cache):
            // ask a supervisor.
            this.pendingDirection = direction;
            this.start(direction, true);
            return;
          }
          // OF-SWITCH-PERM (2026-10-10) — a refusal of a switch that ALREADY
          // carried a supervisor's approval must not reopen the approval
          // dialog: that asked for the password again and again, with no
          // way to see why the hub refused it. Show the hub's own reason.
          this.error = res?.error ?? 'The switch could not be recorded on the store hub.';
          this.refresh();
          return;
        }
        if (direction === 'offline') {
          this.routing.confirmOffline();
        } else {
          this.applyExchangedSession(res.data);
          this.showGoOnlineConfirm = false;
          this.routing.requestGoOnline();
        }
        this.refresh();
      },
      error: () => {
        this.busy = false;
        this.routing.setSwitchFlowActive(false);
        this.error = 'The store hub could not be reached. Check the network and try again.';
        this.refresh();
      },
    });
  }

  /** Go Online: put the cloud session in place of the hub-minted one. */
  private applyExchangedSession(data: any): void {
    const current = this.posState.posAuth;
    if (data?.exchanged === true && data.auth?.token && current) {
      this.posState.setPosSession({
        ...current,
        token: data.auth.token,
        fullName: data.auth.fullName || current.fullName,
        roles: data.auth.roles ?? current.roles,
        canAccessPos: data.auth.canAccessPos ?? current.canAccessPos,
      });
      this.exchangeFailed = false;
      return;
    }
    // A cloud session needs nothing; anything else falls back to PIN once
    // the terminal is back on the cloud.
    this.exchangeFailed = data?.reason !== 'cloud_session' && data?.reason !== 'no_session';
  }

  private deviceId(): string {
    try {
      return this.device.getEdgeClientId();
    } catch {
      return 'unknown';
    }
  }
}
